import { useEffect, useState } from 'react';
import type { PanelCompleto, PanelResumen, UsuarioSesion } from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { COLOR_SEMAFORO, fechaLarga } from '../componentes.js';
import { TareasDelDia } from '../componentes/TareasDelDia.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  usuario: UsuarioSesion;
  idioma: Idioma;
  onAbrirPropiedad?: (id: string) => void;
  onAbrirIncidencia?: (incidenciaId: string, propiedadId: string) => void;
  /** Una tarea del CRM abre la ficha de su oportunidad. */
  onAbrirOportunidad?: (oportunidadId: string) => void;
  /** Al tocar una tarea del dia se va a su proyecto. */
  onAbrirProyecto?: (proyectoId: string) => void;
  /** Al tocar una visita del dia se va a la pestana de visitas de su casa. */
  onAbrirVisita?: (propiedadId: string) => void;
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/*
  Los mismos colores que las pastillas `.chip-nivel`: si la barra del panel y la
  pastilla de la lista pintan N3 de distinto color, el nivel deja de ser una
  senal y pasa a ser decoracion.
*/
const COLOR_NIVEL: Record<string, string> = {
  N1: 'var(--rojo)',
  N2: 'var(--ambar)',
  N3: 'var(--info)',
  N4: 'var(--tinta-suave)',
};

function Kpi({
  etiqueta,
  valor,
  pie,
  tono,
}: {
  etiqueta: string;
  valor: string;
  pie?: string;
  tono?: 'verde' | 'ambar' | 'rojo';
}) {
  return (
    <div className="tarjeta kpi-card">
      <h3>{etiqueta}</h3>
      <span className="kpi-valor">{valor}</span>
      {pie && <span className={`kpi-pie ${tono ?? ''}`}>{pie}</span>}
    </div>
  );
}

/**
 * Cuadro de mando. La portada del sistema: qué reclama atención, qué dinero hay
 * en juego y en qué está el equipo. Todo sale de una sola llamada y de datos
 * reales; los bloques sin datos se muestran vacíos en vez de rellenarse.
 */
export function Dashboard({
  usuario,
  idioma,
  onAbrirPropiedad,
  onAbrirIncidencia,
  onAbrirOportunidad,
  onAbrirProyecto,
  onAbrirVisita,
}: Props) {
  const [panel, setPanel] = useState<PanelResumen | null>(null);
  const [full, setFull] = useState<PanelCompleto | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Los informes por persona necesitan el id; el cumplimiento sólo trae nombres.
  const [usuariosPorNombre, setUsuariosPorNombre] = useState<Record<string, string>>({});

  /*
    EL RENDIMIENTO DEL EQUIPO SE MIRA HACIA ATRAS.

    El resto del panel habla de este mes —lo que llevamos gastado, lo que falta
    por facturar— y ahi mirar otro mes no tiene sentido. Pero «cuantas visitas
    hizo cada uno» es justo la pregunta que se hace a mes cerrado, para pagar y
    para repasar, y hasta ahora no habia forma de ver septiembre en octubre.

    Lleva su propio mes, separado del panel, para que elegirlo no cambie las
    cifras de arriba: son dos preguntas distintas en la misma pantalla.
  */
  const ahora = new Date();
  const [equipoAnio, setEquipoAnio] = useState(ahora.getUTCFullYear());
  const [equipoMes, setEquipoMes] = useState(ahora.getUTCMonth() + 1);
  const [cumplimiento, setCumplimiento] = useState<PanelCompleto['cumplimiento']>([]);

  useEffect(() => {
    api
      .panelCompleto(equipoAnio, equipoMes)
      .then(({ panel: p }) => setCumplimiento(p.cumplimiento))
      .catch(() => setCumplimiento([]));
  }, [equipoAnio, equipoMes]);

  useEffect(() => {
    api
      .panel()
      .then(({ panel: p }) => setPanel(p))
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi ? e.message : t(idioma, 'error.servidor')),
      );
    api
      .panelCompleto()
      .then(({ panel: p }) => setFull(p))
      .catch(() => undefined);
    api
      .usuarios()
      .then((r) =>
        setUsuariosPorNombre(Object.fromEntries(r.usuarios.map((u) => [u.nombre, u.id]))),
      )
      .catch(() => undefined);
  }, [idioma]);

  const dinero = (c: number) =>
    formatearImporteDesdeCents(c, idioma, { moneda: panel?.moneda ?? 'EUR' });

  const maxHoras = Math.max(1, ...(full?.horasPorTrabajador ?? []).map((h) => h.horas));
  const maxNivel = Math.max(1, ...Object.values(full?.incidenciasPorNivel ?? {}));

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'panel.hola', { nombre: usuario.nombre.split(/\s+/)[0] ?? '' })}</h1>
          <p>
            {panel ? `${MESES[panel.mes - 1]} de ${panel.anio} · ` : ''}
            {usuario.tenantNombre}
          </p>
        </div>
        {panel && usuario.rol === 'DIRECTOR' && (
          <div className="acciones">
            <a
              className="boton secundario"
              href={api.urlInformeCartera(panel.anio, panel.mes)}
              target="_blank"
              rel="noreferrer"
            >
              <Icono nombre="documento" /> {t(idioma, 'panel.informeCartera')}
            </a>
          </div>
        )}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {!panel && !error && <p>{t(idioma, 'app.cargando')}</p>}

      {/* Lo primero de la manana es lo que toca hoy, no las cifras del mes. */}
      <TareasDelDia
        idioma={idioma}
        {...(onAbrirProyecto ? { onAbrirProyecto } : {})}
        {...(onAbrirVisita ? { onAbrirVisita } : {})}
        {...(onAbrirIncidencia ? { onAbrirIncidencia } : {})}
        {...(onAbrirOportunidad ? { onAbrirOportunidad } : {})}
      />

      {panel && (
        <>
          <div className="rejilla kpis">
            <Kpi
              etiqueta={t(idioma, 'panel.margenMes')}
              valor={dinero(full?.kpis.margenCents ?? panel.margenMesCents)}
              pie={t(idioma, 'panel.margenPie')}
            />
            <Kpi
              etiqueta={t(idioma, 'panel.costeSoportado')}
              valor={dinero(full?.kpis.costeClientesCents ?? panel.costeMesCents)}
              pie={t(idioma, 'panel.costePie')}
            />
            <Kpi
              etiqueta={t(idioma, 'panel.facturable')}
              valor={dinero(full?.kpis.facturableClientesCents ?? panel.facturableMesCents)}
              pie={t(idioma, 'panel.facturablePie')}
            />
            <Kpi
              etiqueta={t(idioma, 'panel.gastoInterno')}
              valor={dinero(full?.kpis.gastoInternoCents ?? panel.gastosInternosCents)}
              pie={t(idioma, 'panel.gastoInternoPie')}
            />
          </div>

          <div className="rejilla kpis">
            <Kpi
              etiqueta={t(idioma, 'propiedades.titulo')}
              valor={String(panel.propiedades)}
              pie={`${panel.propiedadesActivas} ${t(idioma, 'cartera.activas', { total: panel.propiedadesActivas }).replace(/^\d+\s*/, '')}`}
            />
            <Kpi etiqueta={t(idioma, 'panel.clientes')} valor={String(panel.clientes)} />
            <Kpi
              etiqueta={t(idioma, 'panel.incidenciasCriticas')}
              valor={String(panel.incidenciasCriticas)}
              pie={t(idioma, 'panel.deAbiertas', { total: panel.incidenciasAbiertas })}
              tono={panel.incidenciasCriticas > 0 ? 'rojo' : undefined}
            />
            <Kpi
              etiqueta={t(idioma, 'panel.horasMes')}
              valor={`${panel.horasMes} h`}
              pie={t(idioma, 'panel.horasPie')}
            />
          </div>
        </>
      )}

      {full && (
        <>
          {/* --- Pendiente de facturar ------------------------------------- */}
          {full.pendienteFacturar.length > 0 && (
            <section className="tarjeta">
              <div className="tarjeta-cabeza">
                <h2>{t(idioma, 'panel.pendienteFacturar')}</h2>
                <b className="numerico">{dinero(full.pendienteFacturarTotalCents)}</b>
              </div>
              <p className="suave" style={{ fontSize: '0.8125rem' }}>
                {t(idioma, 'panel.pendienteFacturarNota')}
              </p>
              <div className="lista">
                {full.pendienteFacturar.map((f) => (
                  <button
                    key={f.propiedadId}
                    type="button"
                    className="fila fila-pulsable"
                    onClick={() => onAbrirPropiedad?.(f.propiedadId)}
                  >
                    <span className="crecer">
                      <b>{f.nombre}</b>
                      <small>
                        {f.codigo} · {t(idioma, 'panel.nGastos', { total: f.gastos })} ·{' '}
                        {t(idioma, 'panel.esperando', { total: f.esperaDias })}
                      </small>
                    </span>
                    <span className="numerico">{dinero(f.importeCents)}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* --- Incidencias activas por nivel ------------------------------ */}
          <section className="tarjeta">
            <div className="tarjeta-cabeza">
              <h2>{t(idioma, 'panel.incidenciasActivas')}</h2>
              <span className="suave">{t(idioma, 'panel.total', { total: full.incidenciasTotal })}</span>
            </div>
            <div className="barras">
              {(['N1', 'N2', 'N3', 'N4'] as const).map((n) => (
                <div key={n} className="barra-fila">
                  <span className="barra-etiqueta">
                    {n} — {t(idioma, `nivelIncidencia.${n.slice(1)}`)}
                  </span>
                  <span className="barra-pista">
                    <span
                      className="barra-valor"
                      style={{
                        width: `${((full.incidenciasPorNivel[n] ?? 0) / maxNivel) * 100}%`,
                        background: COLOR_NIVEL[n],
                      }}
                    />
                  </span>
                  <span className="numerico">{full.incidenciasPorNivel[n] ?? 0}</span>
                </div>
              ))}
            </div>
          </section>

          {/* --- Incidencias críticas abiertas ------------------------------ */}
          {full.incidenciasCriticas.length > 0 && (
            <section className="tarjeta">
              <div className="tarjeta-cabeza">
                <h2>{t(idioma, 'panel.criticasAbiertas')}</h2>
                <span className="suave">{full.incidenciasCriticas.length}</span>
              </div>
              <div className="lista">
                {full.incidenciasCriticas.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    className="fila fila-pulsable"
                    onClick={() => onAbrirIncidencia?.(i.id, i.propiedadId)}
                  >
                    <span className={`chip-nivel N${i.nivel}`}>N{i.nivel}</span>
                    <span className="crecer">
                      <b>{i.titulo}</b>
                      <small>
                        {i.codigo} · {i.propiedadNombre}
                      </small>
                    </span>
                    {i.costeEstimadoCents !== null && (
                      <span className="numerico">{dinero(i.costeEstimadoCents)}</span>
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* --- Resumen de propiedades ------------------------------------ */}
          <section className="tarjeta">
            <h2>{t(idioma, 'panel.resumenPropiedades')}</h2>
            <div className="tabla-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t(idioma, 'panel.propiedad')}</th>
                    <th scope="col">NIO</th>
                    <th scope="col">{t(idioma, 'propiedades.estado')}</th>
                    <th scope="col">{t(idioma, 'ficha.ultimaVisita')}</th>
                    <th scope="col">{t(idioma, 'ficha.proximaVisita')}</th>
                    <th scope="col">{t(idioma, 'ficha.incidencias')}</th>
                  </tr>
                </thead>
                <tbody>
                  {full.resumenPropiedades.map((p) => (
                    <tr
                      key={p.id}
                      className="fila-pulsable"
                      onClick={() => onAbrirPropiedad?.(p.id)}
                    >
                      <td>
                        <b>{p.nombre}</b>
                        <br />
                        <span className="suave numerico">{p.codigo}</span>
                      </td>
                      <td>{p.nivelNio === null ? '—' : `NIO ${p.nivelNio}`}</td>
                      <td>
                        <span className="pastilla" style={{ color: COLOR_SEMAFORO[p.semaforo] }}>
                          ● {t(idioma, `semaforo.${p.semaforo}`)}
                        </span>
                      </td>
                      <td>{fechaLarga(p.ultimaVisita, idioma)}</td>
                      <td>{fechaLarga(p.proximaVisita, idioma)}</td>
                      <td>
                        <span className="fila-flex" style={{ gap: '0.25rem', flexWrap: 'wrap' }}>
                          {(['n1', 'n2', 'n3', 'n4'] as const).map((k) =>
                            p.incidencias[k] > 0 ? (
                              <span key={k} className={`chip-nivel ${k.toUpperCase()}`}>
                                {p.incidencias[k]} {k.toUpperCase()}
                              </span>
                            ) : null,
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* --- Ocupación y próximas visitas ------------------------------- */}
          <div className="panel-doble">
            <section className="tarjeta">
              <h2>{t(idioma, 'panel.ocupacion')}</h2>
              <h3>{t(idioma, 'panel.ocupadasAhora', { total: full.ocupadasAhora.length })}</h3>
              {full.ocupadasAhora.length === 0 ? (
                <p className="suave">{t(idioma, 'panel.ningunaOcupada')}</p>
              ) : (
                <div className="lista">
                  {full.ocupadasAhora.map((e) => (
                    <div key={e.propiedadId + e.desde} className="fila">
                      <span className="crecer">
                        <b>{e.nombre}</b>
                        <small>
                          {fechaLarga(e.desde, idioma)} → {fechaLarga(e.hasta, idioma)}
                        </small>
                      </span>
                      <span className="suave">{t(idioma, 'panel.noches', { total: e.noches })}</span>
                    </div>
                  ))}
                </div>
              )}

              <h3>{t(idioma, 'panel.proximasLlegadas', { total: full.proximasLlegadas.length })}</h3>
              {full.proximasLlegadas.length === 0 ? (
                <p className="suave">—</p>
              ) : (
                <div className="lista">
                  {full.proximasLlegadas.map((e) => (
                    <div key={e.propiedadId + e.desde} className="fila">
                      <span className="crecer">
                        <b>{e.nombre}</b>
                        <small>{fechaLarga(e.desde, idioma)}</small>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="tarjeta">
              <h2>{t(idioma, 'panel.proximasVisitas')}</h2>
              {full.proximasVisitas.length === 0 ? (
                <p className="suave">{t(idioma, 'calendario.sinEventos')}</p>
              ) : (
                <div className="lista">
                  {full.proximasVisitas.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className="fila fila-pulsable"
                      onClick={() => onAbrirPropiedad?.(v.propiedadId)}
                    >
                      <Icono nombre="calendario" />
                      <span className="crecer">
                        <b>{v.propiedadNombre}</b>
                        <small>
                          {v.titulo} · {fechaLarga(v.fecha, idioma)}
                          {v.hora ? ` ${v.hora}` : ''}
                        </small>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* --- Carga operativa / horas por trabajador --------------------- */}
          {full.horasPorTrabajador.length > 0 && (
            <section className="tarjeta">
              <h2>{t(idioma, 'panel.cargaOperativa')}</h2>
              <div className="barras">
                {full.horasPorTrabajador.map((h) => (
                  <div key={h.nombre} className="barra-fila">
                    <span className="barra-etiqueta">{h.nombre}</span>
                    <span className="barra-pista">
                      <span
                        className="barra-valor"
                        style={{
                          width: `${(h.horas / maxHoras) * 100}%`,
                          background: 'var(--t-acento)',
                        }}
                      />
                    </span>
                    <span className="numerico">{h.horas}h</span>
                  </div>
                ))}
              </div>

              <h3>{t(idioma, 'panel.horasPorTrabajador')}</h3>
              <div className="lista">
                {full.horasPorTrabajador
                  .filter((h) => h.propiedades.length > 0)
                  .map((h) => (
                    <div key={h.nombre} className="fila">
                      <span className="crecer">
                        <b>{h.nombre}</b>
                        <span className="fila-flex" style={{ gap: '0.25rem', flexWrap: 'wrap' }}>
                          {h.propiedades.slice(0, 8).map((p) => (
                            <span key={p.codigo} className="chip-area">
                              {p.codigo} {p.horas}h
                            </span>
                          ))}
                        </span>
                      </span>
                      <span className="numerico">{h.horas}h</span>
                    </div>
                  ))}
              </div>
            </section>
          )}

          {/* --- Facturas por cobrar ---------------------------------------- */}
          {full.facturasPorCobrar.length > 0 && (
            <section className="tarjeta">
              <div className="tarjeta-cabeza">
                <h2>{t(idioma, 'panel.facturasPorCobrar')}</h2>
                <b className="numerico">{dinero(full.facturasPorCobrarTotalCents)}</b>
              </div>
              <div className="lista">
                {full.facturasPorCobrar.map((f) => (
                  <div key={f.id} className="fila">
                    <Icono nombre="factura" />
                    <span className="crecer">
                      <b>
                        {f.numero} · {f.propiedadNombre}
                      </b>
                      <small>
                        {f.fechaEmision ? t(idioma, 'panel.emitidaEl', { fecha: f.fechaEmision }) : ''}
                      </small>
                    </span>
                    <span className="numerico">{dinero(f.totalCents)}</span>
                    {f.diasVencida !== null && f.diasVencida > 0 && (
                      <span className="pastilla rojo">
                        {t(idioma, 'factura.vencidaDias', { total: f.diasVencida })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* --- Rendimiento del equipo -------------------------------------- */}
          {cumplimiento.length > 0 && (
            <section className="tarjeta">
              <div className="tarjeta-cabeza">
                <h2>{t(idioma, 'panel.rendimientoEquipo')}</h2>
                {/*
                  El mes, aqui mismo. Se mira a mes cerrado —para pagar y para
                  repasar— asi que septiembre tiene que alcanzarse en octubre.
                */}
                <div className="equipo-periodo">
                  <select
                    aria-label={t(idioma, 'fin.mes')}
                    value={equipoMes}
                    onChange={(e) => setEquipoMes(Number(e.target.value))}
                  >
                    {MESES.map((nombre, i) => (
                      <option key={nombre} value={i + 1}>
                        {nombre}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label={t(idioma, 'fin.anio')}
                    value={equipoAnio}
                    onChange={(e) => setEquipoAnio(Number(e.target.value))}
                  >
                    {[equipoAnio - 1, equipoAnio, equipoAnio + 1].map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="suave" style={{ fontSize: '0.8125rem' }}>
                {t(idioma, 'panel.cumplimientoNota')}
              </p>
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">{t(idioma, 'interno.persona')}</th>
                      <th scope="col" className="num">
                        {t(idioma, 'panel.agendadas')}
                      </th>
                      <th scope="col" className="num">
                        {t(idioma, 'panel.completadas')}
                      </th>
                      <th scope="col" className="num">
                        {t(idioma, 'panel.cumplimiento')}
                      </th>
                      <th scope="col" className="num">
                        {t(idioma, 'ficha.horasCol')}
                      </th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {cumplimiento.map((c) => (
                      <tr key={c.nombre}>
                        <td>{c.nombre}</td>
                        <td className="num numerico">{c.agendadas}</td>
                        <td className="num numerico">{c.completadas}</td>
                        <td className="num numerico">
                          {c.porcentaje < 0 ? (
                            <span className="suave">{t(idioma, 'panel.sinAgenda')}</span>
                          ) : (
                            `${c.porcentaje}%`
                          )}
                        </td>
                        <td className="num numerico">{c.horas}h</td>
                        <td>
                          {usuario.rol === 'DIRECTOR' && (
                            <a
                              className="enlace"
                              href={api.urlInformePersona(
                                usuariosPorNombre[c.nombre] ?? '',
                                equipoAnio,
                                equipoMes,
                              )}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Icono nombre="documento" /> PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* --- Actividad reciente ----------------------------------------- */}
          {full.actividadReciente.length > 0 && (
            <section className="tarjeta">
              <h2>{t(idioma, 'panel.actividadReciente')}</h2>
              <div className="lista">
                {full.actividadReciente.map((a) => (
                  <div key={a.id} className="fila">
                    <span className="crecer">
                      <b>{a.titulo}</b>
                      <small>
                        {a.codigo}
                        {a.detalle ? ` · ${a.detalle}` : ''}
                      </small>
                    </span>
                    <span className="pequeno suave numerico">{fechaLarga(a.ocurridoEn, idioma)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
